import {intlShape, utils} from '@wallaby-ui/core';
import {cloneDeep, isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {defineMessages, injectIntl} from 'react-intl';
import {connect} from 'react-redux';

import SavableComponent from './SavableComponent';
import {
  changeData,
  changeScreenProperty,
  dataRemoved,
  getData,
  indexRemoveItem,
  removeData,
  resetData,
  checkCurrentIndex,
  resetCheckedIndexState,
  resetConflictLastChangeAuthor,
  saveData,
  validateData,
} from '../auto_layout_actions';
import {
  filterArrayItemsDataChanges,
  getScreenBannerConfig,
  getScreenPropertiesByScreenId,
} from '../auto_layout_selectors';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {AutoLayoutService} from 'src/js/lib/auto_layout';
import {ContentComponent} from 'src/js/model/auto_layout_content_schema';
import {LayoutComponent} from 'src/js/model/auto_layout_layout_schema';
import {KEYWORD_DATA_CONTENT} from 'src/js/model/auto_layout_screen_data_helpers';

const logger = utils.LogUtils.getLogger(
  'SavableComponentContainer',
  LIBRARY_NAME
);

export const I18N = defineMessages({
  CONFIRM_DELETE: {
    id: 'autoLayout.screen.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this screen?',
    description: 'text used in confirm dialog for removing screen',
  },
  CONFIRM_RELOAD_AFTER_DELETE_CONFLICT: {
    id: 'autoLayout.invalid.text.afterDelete',
    defaultMessage:
      'We are sorry, but deleted screen was edited by another user.' +
      ' Those changes have to be loaded first and then you can repeat your action.',
    description: 'text for invalid screen after delete in auto layout',
  },
  CONFIRM_RELOAD_AFTER_SAVE_CONFLICT: {
    id: 'autoLayout.invalid.text.afterSave',
    defaultMessage:
      'Data was modified by another user. The form should be reloaded.',
    description: 'text for invalid screen after save in auto layout',
  },
  CONFIRM_RESET: {
    id: 'autoLayout.screen.confirmReset',
    defaultMessage:
      'Are you sure you want to return your changes of this screen?',
    description: 'text used in confirm dialog for resetting screen',
  },
  ERROR_LOAD: {
    id: 'autoLayout.screen.error.load',
    defaultMessage: 'Unable to load data. Please contact your administrator.',
    description: 'default error message on failed screen load',
  },
  ERROR_REMOVE: {
    id: 'autoLayout.screen.error.remove',
    defaultMessage: 'Unable to delete data. Please contact your administrator.',
    description: 'default error message on failed screen delete',
  },
  ERROR_SAVE: {
    id: 'autoLayout.screen.error.save',
    defaultMessage: 'Unable to save data. Please contact your administrator.',
    description: 'default error message on failed screen save',
  },
});

class SavableComponentContainer extends React.Component {
  constructor(props) {
    super(props);

    this.handleCancelConfirm = this.handleCancelConfirm.bind(this);
    this.handleCollapse = this.handleCollapse.bind(this);
    this.handleDataChange = this.handleDataChange.bind(this);
    this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleSaveScreen = this.handleSaveScreen.bind(this);
    this.saveData = this.saveData.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleReload = this.handleReload.bind(this);
    this.handleReloadConfirm = this.handleReloadConfirm.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleResetConfirm = this.handleResetConfirm.bind(this);
    this.handleSaveWarningConfirm = this.handleSaveWarningConfirm.bind(this);
    this.performDeleteScreen = this.performDeleteScreen.bind(this);

    this.state = {
      isIndexCheckingBySave: false,
      isIndexCheckingByDelete: false,
      showChangesInConfirm: false,
      showConfirm: false,
      showConfirmCancel: true,
      confirmText: '',
      onConfirm: this.handleDeleteConfirm,
    };
  }

  componentDidUpdate(prevProps) {
    const {screenProperties, indexChecking} = this.props;
    const {isIndexCheckingBySave, isIndexCheckingByDelete, showConfirm} =
      this.state;
    const {isInvalid} = screenProperties;
    if (
      isIndexCheckingBySave &&
      indexChecking &&
      indexChecking !== prevProps.indexChecking &&
      !showConfirm
    ) {
      this.handleSaveScreen();
    }
    if (
      isIndexCheckingByDelete &&
      indexChecking &&
      indexChecking !== prevProps.indexChecking &&
      !showConfirm
    ) {
      this.performDeleteScreen();
    }
    if (
      isInvalid &&
      isInvalid !== prevProps.screenProperties.isInvalid &&
      !showConfirm
    ) {
      this.handleReload();
    }
  }

  handleDataChange(dataChange) {
    const {autoLayoutStateId, screenId} = this.props;
    this.props.changeData(autoLayoutStateId, screenId, dataChange);
  }

  handleCancelConfirm() {
    this.closeConfirm();
  }

  handleSaveWarningConfirm() {
    this.closeConfirm();
    this.saveData();
  }

  handleSave() {
    const {autoLayoutStateId, config, contextParams, screenId} = this.props;
    const coreScreenId = AutoLayoutService.getCoreScreenId(screenId);
    // load index before screen save
    this.props.checkCurrentIndex(
      autoLayoutStateId,
      config.service.url.autoLayout,
      coreScreenId,
      contextParams,
      {invokerScreenId: screenId, bySave: true}
    );
    this.setState({isIndexCheckingBySave: true});
  }

  handleSaveScreen() {
    const {
      autoLayoutStateId,
      contextParams,
      data,
      indexChecking,
      intl,
      metadata,
      screenId,
      screenProperties,
    } = this.props;
    const {isFromDb} = screenProperties;

    // reset index checking state
    this.setState({isIndexCheckingBySave: false});

    if (
      indexChecking &&
      indexChecking.invokeInfo &&
      indexChecking.invokeInfo.bySave &&
      (!indexChecking.itemsDifferences ||
        (isEmpty(indexChecking.itemsDifferences.added) &&
          isEmpty(indexChecking.itemsDifferences.removed)))
    ) {
      // reset index checkings and lastChange author, if there wasn't a conflict
      this.props.resetCheckedIndexState(
        autoLayoutStateId,
        AutoLayoutService.getIndexScreenId(
          AutoLayoutService.getCoreScreenId(screenId),
          contextParams
        )
      );
      this.props.resetConflictLastChangeAuthor(
        autoLayoutStateId,
        indexChecking.invokeInfo.invokerScreenId
      );
    }

    // if screen is new or index was not changed by another user, save screen
    if (
      !isFromDb ||
      !indexChecking ||
      (indexChecking.invokeInfo &&
        indexChecking.invokeInfo.bySave &&
        (!indexChecking.itemsDifferences ||
          (isEmpty(indexChecking.itemsDifferences.added) &&
            isEmpty(indexChecking.itemsDifferences.removed))))
    ) {
      // TODO: should be handled in action creator
      // perform validation
      if (metadata.itemContent.isValidated()) {
        const validationPromise = new Promise((resolve) => {
          resolve(this.props.validateData(autoLayoutStateId, screenId, data));
        });

        validationPromise.then((result) => {
          const {errors, warnings} = result;
          if (Object.keys(errors).length === 0) {
            if (warnings.length > 0) {
              const messages = warnings
                .map((message) =>
                  utils.TranslateUtils.translate({id: message}, intl)
                )
                .join('\n');
              this.setState({
                showChangesInConfirm: false,
                showConfirm: true,
                showConfirmCancel: true,
                confirmText: messages,
                onConfirm: this.handleSaveWarningConfirm,
              });
            } else {
              this.saveData();
            }
          }
        });
      } else {
        this.saveData();
      }
    }
  }

  handleDeleteConfirm() {
    this.closeConfirm();
    this.performDelete();
  }

  handleDelete() {
    const {intl} = this.props;
    this.setState({
      showChangesInConfirm: false,
      showConfirm: true,
      showConfirmCancel: true,
      confirmText: utils.TranslateUtils.translate(
        {id: I18N.CONFIRM_DELETE.id},
        intl
      ),
      onConfirm: this.handleDeleteConfirm,
    });
  }

  handleCollapse() {
    const {autoLayoutStateId, screenProperties, screenId} = this.props;
    const {isCollapsed} = screenProperties;
    this.props.changeScreenProperty(autoLayoutStateId, screenId, {
      isCollapsed: !isCollapsed,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  handleDirtyCheckingAfterSave() {
    // const {data} = this.props;
    // TODO:  inform child controls about saving
    // DirtyCheckingService.setSavedOrDeletedFlag(this);
    // DirtyCheckingService.registerDirtyChange(this.contextParams, this.screenId, false);
    // TODO reset dirty flag
    // this.loadedDataContent = _.cloneDeep(data[KEYWORD_DATA_CONTENT]);
  }

  handleEdit() {
    const {autoLayoutStateId, screenId, screenProperties} = this.props;
    const {isEditing} = screenProperties;
    this.props.changeScreenProperty(autoLayoutStateId, screenId, {
      isEditing: !isEditing,
      isCollapsed: isEditing,
    });
  }

  handleResetConfirm() {
    this.closeConfirm();
    this.performReset();
  }

  handleReset() {
    const {dataChanges, intl, isSaved} = this.props;
    if (isEmpty(dataChanges) && isSaved) {
      this.performReset();
    } else {
      this.setState({
        showChangesInConfirm: true,
        showConfirm: true,
        showConfirmCancel: true,
        confirmText: utils.TranslateUtils.translate(
          {id: I18N.CONFIRM_RESET.id},
          intl
        ),
        onConfirm: this.handleResetConfirm,
      });
    }
  }

  handleReloadConfirm() {
    this.closeConfirm();
    this.performReload();
  }

  handleReload() {
    const {intl, screenProperties} = this.props;
    const {invalidByDelete} = screenProperties;
    this.setState({
      showChangesInConfirm: !invalidByDelete,
      showConfirm: true,
      showConfirmCancel: false,
      confirmText: utils.TranslateUtils.translate(
        {
          id: invalidByDelete
            ? I18N.CONFIRM_RELOAD_AFTER_DELETE_CONFLICT.id
            : I18N.CONFIRM_RELOAD_AFTER_SAVE_CONFLICT.id,
        },
        intl
      ),
      onConfirm: this.handleReloadConfirm,
    });
  }

  saveData() {
    const {
      autoLayoutStateId,
      config,
      content,
      contextParams,
      data,
      intl,
      onSave,
      screenId,
    } = this.props;

    // TODO: feature of componentDataPromises are ignored for now
    this.componentDataPromises = [];

    // wait for resolving of all component promises
    return Promise.all(this.componentDataPromises)
      .then(() => {
        // store (in data) toString value of core object from content
        AutoLayoutService.reevaluateToStringFromData(
          data,
          content.getToString()
        );

        // clear component promises
        this.componentDataPromises.length = 0;

        // TODO: check if the clone deep is necessary
        return this.props
          .saveData(
            autoLayoutStateId,
            config.service.url.autoLayout,
            screenId,
            cloneDeep(data),
            contextParams,
            utils.TranslateUtils.translate(I18N.ERROR_SAVE, intl)
          )
          .then(
            (savedDataResponse) => {
              if (onSave) {
                // call callback for parent scopes
                onSave(screenId);
              }
              this.handleDirtyCheckingAfterSave();
              if (!savedDataResponse.isConflictRequestResponse) {
                // do not switch to view mode in case of save conflict
                this.props.changeScreenProperty(autoLayoutStateId, screenId, {
                  useDefaultCollapse: false,
                  isEditing: false,
                  isCollapse: false,
                });
              }
            },
            (error) => {
              logger.error(
                `Saving of the screen ${screenId} has failed (${error}).`
              );
            }
          );
      })
      .catch((error) => {
        logger.error(
          `Promise of some underlying screen component has failed, the screen was not saved (${error}).`
        );
      });
  }

  closeConfirm() {
    this.setState({
      showConfirm: false,
    });
  }

  removeScreen() {
    let removePromise;
    // const screenDataPromise = new Promise((resolve) => {
    //    resolve(screenDataMock);
    // });
    const {
      autoLayoutStateId,
      config,
      contextParams,
      data,
      intl,
      onRemove,
      screenId,
      screenProperties,
    } = this.props;
    const {isFromDb} = screenProperties;

    if (isFromDb) {
      // TODO: check if the clone deep is necessary
      removePromise = this.props
        .removeData(
          autoLayoutStateId,
          config.service.url.autoLayout,
          screenId,
          cloneDeep(data),
          contextParams,
          utils.TranslateUtils.translate(I18N.ERROR_REMOVE, intl)
        )
        .then(
          (removedDataResponse) => {
            if (!removedDataResponse.isConflictRequestResponse) {
              // do not call parent and switch to view mode in case of remove conflict
              if (onRemove) {
                // call callback for parent scopes
                onRemove(screenId);
              }
              this.props.changeScreenProperty(autoLayoutStateId, screenId, {
                useDefaultCollapse: false,
                isEditing: false,
                isCollapse: false,
              });
            }
          },
          (error) => {
            logger.error(
              `Removing of the screen ${screenId} has failed (${error}).`
            );
          }
        );
    } else {
      removePromise = new Promise((resolve) => {
        if (onRemove) {
          // call callback for parent scopes
          onRemove(screenId);
        }
        this.props.dataRemoved(autoLayoutStateId, screenId);
        resolve();
      });
    }
    return removePromise
      .then(() => {
        this.handleDirtyCheckingAfterSave();
      })
      .catch((error) => {
        logger.error(
          `Removing of the screen ${screenId} has failed (${error}).`
        );
      });
  }

  performDelete() {
    const {autoLayoutStateId, config, contextParams, screenId} = this.props;
    const coreScreenId = AutoLayoutService.getCoreScreenId(screenId);
    // load index before screen delete
    this.props.checkCurrentIndex(
      autoLayoutStateId,
      config.service.url.autoLayout,
      coreScreenId,
      contextParams,
      {invokerScreenId: screenId, byDelete: true}
    );
    this.setState({isIndexCheckingByDelete: true});
  }

  performDeleteScreen() {
    const {
      autoLayoutStateId,
      contextParams,
      indexChecking,
      screenId,
      screenProperties,
    } = this.props;
    const {isFromDb} = screenProperties;

    // reset index checking state
    this.setState({isIndexCheckingByDelete: false});

    if (
      indexChecking &&
      indexChecking.invokeInfo &&
      indexChecking.invokeInfo.byDelete &&
      (!indexChecking.itemsDifferences ||
        (isEmpty(indexChecking.itemsDifferences.added) &&
          isEmpty(indexChecking.itemsDifferences.removed)))
    ) {
      // reset index checkings and lastChange author, if there wasn't a conflict
      this.props.resetCheckedIndexState(
        autoLayoutStateId,
        AutoLayoutService.getIndexScreenId(
          AutoLayoutService.getCoreScreenId(screenId),
          contextParams
        )
      );
      this.props.resetConflictLastChangeAuthor(
        autoLayoutStateId,
        indexChecking.invokeInfo.invokerScreenId
      );
    }

    // if screen is new or index was not changed by another user, remove screen
    if (
      !isFromDb ||
      !indexChecking ||
      (indexChecking.invokeInfo &&
        indexChecking.invokeInfo.byDelete &&
        (!indexChecking.itemsDifferences ||
          (isEmpty(indexChecking.itemsDifferences.added) &&
            isEmpty(indexChecking.itemsDifferences.removed))))
    ) {
      this.removeScreen();
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  performReset() {
    const {autoLayoutStateId, contextParams, isSaved, screenId} = this.props;
    this.props.resetData(autoLayoutStateId, screenId);
    this.props.changeScreenProperty(autoLayoutStateId, screenId, {
      isEditing: false,
    });
    if (!isSaved) {
      this.props.dataRemoved(autoLayoutStateId, screenId);
      // remove new empty screen from index
      this.props.indexRemoveItem(
        autoLayoutStateId,
        AutoLayoutService.getIndexScreenId(
          AutoLayoutService.getCoreScreenId(screenId),
          contextParams
        ),
        AutoLayoutService.getIdFromScreenId(screenId)
      );
    }
  }

  performReload() {
    const {
      autoLayoutStateId,
      config,
      content,
      contextParams,
      intl,
      layout,
      metadata,
      onReload,
      screenId,
    } = this.props;
    if (contextParams) {
      if (onReload) {
        // call callback for parent scopes (reload index)
        onReload(screenId);
      }

      // throw away all screen changes
      this.props.resetData(autoLayoutStateId, screenId);
      this.props.changeScreenProperty(autoLayoutStateId, screenId, {
        isEditing: false,
      });
      // reload only this screen
      this.props.getData(
        autoLayoutStateId,
        config.service.url.autoLayout,
        screenId,
        contextParams,
        utils.TranslateUtils.translate(I18N.ERROR_LOAD, intl),
        false,
        true,
        metadata.getContent(),
        content,
        layout
      );
    }
  }

  isNotEditable() {
    const {readOnly, screenProperties} = this.props;
    const {isRemoving, isSaving} = screenProperties;
    return readOnly || isRemoving || isSaving;
  }

  render() {
    const {
      autoLayoutStateId,
      conflictLastChangeAuthor,
      content,
      data,
      dataChanges,
      dataSets,
      errors,
      expanded,
      filteredArrayItemsDataChanges,
      isIndexInvalid,
      isSaved,
      layout,
      metadata,
      readOnly,
      screenBannerConfig,
      screenId,
      screenProperties,
      textOnly,
    } = this.props;
    const {isCollapsed, isEditing, isFromDb, useDefaultCollapse} =
      screenProperties;
    const {
      showChangesInConfirm,
      showConfirm,
      confirmText,
      onConfirm,
      showConfirmCancel,
    } = this.state;
    let isReallyCollapsed = isCollapsed;
    if (useDefaultCollapse && isFromDb) {
      isReallyCollapsed = layout && layout.getCollapsedDefault();
    }

    return (
      <SavableComponent
        autoLayoutStateId={autoLayoutStateId}
        screenBannerConfigProps={screenBannerConfig.getConfigurationByData(
          data ? data[KEYWORD_DATA_CONTENT] : undefined
        )}
        confirmText={confirmText}
        content={content}
        dataSets={dataSets}
        errors={errors}
        isCollapsed={isReallyCollapsed}
        isIndexInvalid={isIndexInvalid}
        isSaved={isSaved}
        layout={layout}
        metadata={metadata}
        screenId={screenId}
        rootData={data}
        dataChanges={dataChanges}
        conflictLastChangeAuthor={conflictLastChangeAuthor}
        filteredArrayItemsDataChanges={filteredArrayItemsDataChanges}
        pathToData={''}
        readOnly={readOnly || !isEditing}
        textOnly={textOnly || !isEditing}
        expanded={expanded}
        screenProperties={screenProperties}
        showChangesInConfirm={showChangesInConfirm}
        showConfirm={showConfirm}
        showConfirmCancel={showConfirmCancel}
        onCancelConfirm={this.handleCancelConfirm}
        onCollapse={this.handleCollapse}
        onConfirm={onConfirm}
        onDataChange={this.handleDataChange}
        onDelete={this.handleDelete}
        onEdit={this.handleEdit}
        onReload={this.handleReload}
        onReset={this.handleReset}
        onSave={this.handleSave}
        notEditable={this.isNotEditable()}
      />
    );
  }
}

SavableComponentContainer.displayName = 'SavableComponentContainer';

/* eslint-disable react/no-unused-prop-types */
SavableComponentContainer.propTypes = {
  autoLayoutStateId: PropTypes.string.isRequired,
  config: PropTypes.objectOf(PropTypes.any).isRequired,
  configStateId: PropTypes.string.isRequired,
  contextParams: PropTypes.objectOf(PropTypes.any).isRequired,
  content: PropTypes.instanceOf(ContentComponent).isRequired,
  intl: intlShape.isRequired,
  isIndexInvalid: PropTypes.bool,
  isSaved: PropTypes.bool,
  layout: PropTypes.instanceOf(LayoutComponent).isRequired,
  metadata: PropTypes.objectOf(PropTypes.any).isRequired,
  screenId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  textOnly: PropTypes.bool,
  expanded: PropTypes.bool,
  onReload: PropTypes.func,
  onRemove: PropTypes.func,
  onSave: PropTypes.func,
};

SavableComponentContainer.defaultProps = {
  isIndexInvalid: false,
  isSaved: true,
  readOnly: false,
  textOnly: false,
  expanded: false,
  onReload: undefined,
  onRemove: undefined,
  onSave: undefined,
};

export default connect(
  (state, props) => ({
    conflictLastChangeAuthor:
      state[props.autoLayoutStateId].conflictLastChangeAuthor[props.screenId],
    errors: state[props.autoLayoutStateId].errors[props.screenId],
    data: state[props.autoLayoutStateId].dataCache[props.screenId],
    dataChanges: state[props.autoLayoutStateId].dataChanges[props.screenId],
    filteredArrayItemsDataChanges: filterArrayItemsDataChanges(state, props),
    dataSets: state[props.configStateId].dataSets,
    indexChecking:
      state[props.autoLayoutStateId].indexCheckings[
        AutoLayoutService.getIndexScreenId(
          AutoLayoutService.getCoreScreenId(props.screenId),
          props.contextParams
        )
      ],
    screenBannerConfig: getScreenBannerConfig(state, props),
    screenProperties: getScreenPropertiesByScreenId(state, props),
  }),
  {
    changeData,
    changeScreenProperty,
    dataRemoved,
    indexRemoveItem,
    getData,
    removeData,
    resetCheckedIndexState,
    resetConflictLastChangeAuthor,
    resetData,
    checkCurrentIndex,
    saveData,
    validateData,
  },
  null,
  {forwardRef: true}
)(injectIntl(SavableComponentContainer, {forwardRef: true}));
